import { graphQlClient } from '@config/graphqlClient';
import UserOrganizationGraphQL from '@graphql/userOrganization.queries';
import UserGraphQL from '@graphql/user.queries';
import SeatGraphQL from '@graphql/seat.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Organization } from '@models/organization.model';
import { assignSeatRequest, revokeSeatRequest, SeatType } from '@models/seat.model';
import {
  UserRoles,
  UserStatus,
  getUserOrganizationsWithDeletedRequest,
  restoreUserToOrganizationRequest,
  disableOrEnableUserRequest,
} from '@models/user.model';
import { DeleteOutline, EditOutlined, Add as AddIcon, NoAccounts, RestoreFromTrash, EventSeat } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { RootState } from '@redux/store';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import WolfTooltip from '../ui/WolfTooltip';

interface UserOrganization {
  userOrganizationId: string;
  organizationId: string;
  organization: {
    organizationId: string;
    name: string;
    description?: string;
  };
  role: UserRoles;
  status?: UserStatus;
  deletedAt?: Date;
  hasSeat?: boolean;
  seatType?: SeatType;
  seatAssignmentId?: string;
}

interface UserOrganizationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userEmail: string;
  organizations: Organization[]; // All available organizations
}

const UserOrganizationsModal: React.FC<UserOrganizationsModalProps> = ({
  isOpen,
  onClose,
  userId,
  userEmail,
  organizations,
}) => {
  const [allUserOrganizations, setAllUserOrganizations] = useState<UserOrganization[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [newRole, setNewRole] = useState<UserRoles>(UserRoles.USER);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [addingRole, setAddingRole] = useState<UserRoles>(UserRoles.USER);

  const dispatch = useDispatch();
  const { role: currentUserRole } = useAppSelector((state: RootState) => state.user);
  const localeCommon = useTranslation('common');
  const localeUsers = useTranslation('users');
  const localeUserOrgs = useTranslation('userOrganizations');
  const localeActionMessages = useTranslation('actionMessages');

  const isSuperAdmin = currentUserRole === UserRoles.SUPER_ADMIN;

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserOrganizations();
    }
  }, [isOpen, userId]);

  const fetchUserOrganizations = async () => {
    if (!isSuperAdmin) {
      // For non-super admins, we can't fetch all user organizations
      // They should only see the user from their current organization context
      return;
    }

    setLoading(true);
    try {
      const data: getUserOrganizationsWithDeletedRequest = await graphQlClient.request(
        UserOrganizationGraphQL.queries.getUserOrganizationsWithDeleted,
        { userId },
      );

      setAllUserOrganizations(data.getUserOrganizationsWithDeleted || []);
    } catch (error: any) {
      dispatch(
        appendActionMessage({
          message: error?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userOrganizationId: string, role: UserRoles) => {
    if (!isSuperAdmin) return;

    try {
      await graphQlClient.request(
        UserOrganizationGraphQL.mutations.updateUserOrganizationRole,
        {
          input: {
            userOrganizationId,
            role,
          },
        },
      );

      // Update local state
      setAllUserOrganizations((prev) =>
        prev.map((uo) => (uo.userOrganizationId === userOrganizationId ? { ...uo, role } : uo)),
      );

      dispatch(
        appendActionMessage({
          message: localeActionMessages['userRoleUpdatedSuccessfully'],
          type: SnackType.SUCCESS,
        }),
      );

      setEditingRole(null);
    } catch (error: any) {
      dispatch(
        appendActionMessage({
          message: error?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const handleRemoveFromOrganization = async (userOrganizationId: string, organizationId: string) => {
    if (!isSuperAdmin) return;

    try {
      await graphQlClient.request(
        UserOrganizationGraphQL.mutations.removeUserFromOrganization,
        { userId, organizationId },
      );

      // Update local state - mark as deleted
      setAllUserOrganizations((prev) =>
        prev.map((uo) => (uo.userOrganizationId === userOrganizationId ? { ...uo, deletedAt: new Date() } : uo)),
      );

      dispatch(
        appendActionMessage({
          message: localeActionMessages['userRemovedFromOrganizationSuccessfully'],
          type: SnackType.SUCCESS,
        }),
      );
    } catch (error: any) {
      dispatch(
        appendActionMessage({
          message: error?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const handleAddToOrganization = async () => {
    if (!isSuperAdmin || !selectedOrgId) return;

    try {
      await graphQlClient.request(
        UserOrganizationGraphQL.mutations.addUserToOrganization,
        {
          input: {
            userId,
            organizationId: selectedOrgId,
            role: addingRole,
          },
        },
      );

      // Refresh the list
      await fetchUserOrganizations();

      dispatch(
        appendActionMessage({
          message: localeActionMessages['userAddedToOrganizationSuccessfully'],
          type: SnackType.SUCCESS,
        }),
      );

      setShowAddDialog(false);
      setSelectedOrgId('');
      setAddingRole(UserRoles.USER);
    } catch (error: any) {
      dispatch(
        appendActionMessage({
          message: error?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const handleRestoreToOrganization = async (organizationId: string, currentRole: UserRoles) => {
    if (!isSuperAdmin) return;

    try {
      const data: restoreUserToOrganizationRequest = await graphQlClient.request(
        UserOrganizationGraphQL.mutations.restoreUserToOrganization,
        { userId, organizationId, role: currentRole },
      );

      if (data.restoreUserToOrganization) {
        // Update local state - remove deletedAt
        setAllUserOrganizations((prev) =>
          prev.map((uo) =>
            uo.organizationId === organizationId && uo.deletedAt
              ? { ...uo, deletedAt: undefined, role: currentRole }
              : uo,
          ),
        );

        dispatch(
          appendActionMessage({
            message: localeActionMessages['userRestoredToOrganizationSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        appendActionMessage({
          message: error?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const handleDisableUserInOrganization = async (organizationId: string) => {
    if (!isSuperAdmin) return;

    try {
      const data: disableOrEnableUserRequest = await graphQlClient.request(
        UserGraphQL.mutations.disableUser,
        { id: userId, organizationId },
      );

      if (data.disableUser) {
        setAllUserOrganizations((prev) =>
          prev.map((uo) =>
            uo.organizationId === organizationId ? { ...uo, status: UserStatus.DISABLED } : uo,
          ),
        );

        dispatch(
          appendActionMessage({
            message: localeUsers['userDisabledSuccessfully'] || 'User disabled successfully',
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        appendActionMessage({
          message: error?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const handleEnableUserInOrganization = async (organizationId: string) => {
    if (!isSuperAdmin) return;

    try {
      const data: disableOrEnableUserRequest = await graphQlClient.request(
        UserGraphQL.mutations.enableUser,
        { id: userId, organizationId },
      );

      if (data.enableUser) {
        setAllUserOrganizations((prev) =>
          prev.map((uo) =>
            uo.organizationId === organizationId ? { ...uo, status: UserStatus.ACTIVE } : uo,
          ),
        );

        dispatch(
          appendActionMessage({
            message: localeUsers['userEnabledSuccessfully'] || 'User enabled successfully',
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        appendActionMessage({
          message: error?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const handleAssignSeat = async (organizationId: string, userRole: UserRoles) => {
    if (!isSuperAdmin) return;

    try {
      const seatType = userRole === UserRoles.ADMIN || userRole === UserRoles.OWNER ? SeatType.ADMIN : SeatType.USER;

      const result: assignSeatRequest = await graphQlClient.request(SeatGraphQL.mutations.assignSeat, {
        input: {
          userId,
          organizationId,
          seatType,
        },
      });

      if (result?.assignSeat?.id) {
        setAllUserOrganizations((prev) =>
          prev.map((uo) =>
            uo.organizationId === organizationId
              ? { ...uo, hasSeat: true, seatType, seatAssignmentId: result.assignSeat.id }
              : uo,
          ),
        );

        dispatch(
          appendActionMessage({
            message: localeUsers['seatAssigned'] || 'Seat assigned successfully',
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        appendActionMessage({
          message: error?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const handleRevokeSeat = async (organizationId: string, seatAssignmentId?: string) => {
    if (!isSuperAdmin || !seatAssignmentId) return;

    try {
      const result: revokeSeatRequest = await graphQlClient.request(SeatGraphQL.mutations.revokeSeat, {
        input: {
          seatAssignmentId,
        },
      });

      if (result?.revokeSeat?.id) {
        setAllUserOrganizations((prev) =>
          prev.map((uo) =>
            uo.organizationId === organizationId
              ? { ...uo, hasSeat: false, seatType: undefined, seatAssignmentId: undefined }
              : uo,
          ),
        );

        dispatch(
          appendActionMessage({
            message: localeUsers['seatRevoked'] || 'Seat revoked successfully',
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        appendActionMessage({
          message: error?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  // Split organizations into active and deleted
  const activeUserOrganizations = allUserOrganizations.filter((uo) => !uo.deletedAt);
  const deletedUserOrganizations = allUserOrganizations.filter((uo) => uo.deletedAt);

  const availableOrganizations = organizations.filter(
    (org) => !activeUserOrganizations.some((uo) => uo.organizationId === org.organizationId),
  );

  const canEditRole = (role: UserRoles) => {
    // Super admins can edit all roles except OWNER (to prevent headless organizations)
    return isSuperAdmin && role !== UserRoles.OWNER;
  };

  const canRemoveFromOrg = (role: UserRoles) => {
    // Super admins can remove users except OWNERS (to prevent headless organizations)
    return isSuperAdmin && role !== UserRoles.OWNER;
  };

  const handleEditRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedRole = e.target.value as UserRoles;

    if (selectedRole === UserRoles.OWNER && !isSuperAdmin) {
      dispatch(
        appendActionMessage({
          message: localeUserOrgs['ownerRoleCannotBeModified'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    setNewRole(selectedRole);
  };

  const handleAddRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedRole = e.target.value as UserRoles;

    if (selectedRole === UserRoles.OWNER && !isSuperAdmin) {
      dispatch(
        appendActionMessage({
          message: localeUserOrgs['ownerRoleCannotBeModified'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    setAddingRole(selectedRole);
  };

  if (!isSuperAdmin) {
    return (
      <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{localeUserOrgs['title']}</DialogTitle>
        <DialogContent>
          <Typography color="textSecondary">
            {localeUserOrgs['viewOnlyMessage']}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{localeCommon['close']}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{localeUserOrgs['organizationsFor']} {userEmail}</Typography>
            <Button startIcon={<AddIcon />} onClick={() => setShowAddDialog(true)} variant="outlined" size="small">
              {localeUserOrgs['addToOrganization']}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Typography>{localeUserOrgs['loading']}</Typography>
          ) : activeUserOrganizations.length === 0 && deletedUserOrganizations.length === 0 ? (
            <Typography color="textSecondary">{localeUserOrgs['noOrganizations']}</Typography>
          ) : (
            <>
              {/* Active Organizations Section */}
              {activeUserOrganizations.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {localeUserOrgs['currentOrganizations']}
                  </Typography>
                  <List>
                    {activeUserOrganizations.map((userOrg) => (
                      <ListItem key={userOrg.userOrganizationId} component={Paper} sx={{ mb: 1 }}>
                        <ListItemText
                          primary={userOrg.organization.name}
                          secondary={userOrg.organization.description}
                        />
                        <Box display="flex" flexDirection="column" alignItems="end" gap={1}>
                          {editingRole === userOrg.userOrganizationId ? (
                            <Box display="flex" gap={1} alignItems="center">
                              <TextField
                                select
                                size="small"
                                value={newRole}
                                onChange={handleEditRoleChange}
                                sx={{ minWidth: 120 }}>
                                {Object.values(UserRoles)
                                  .filter((role) => isSuperAdmin || role !== UserRoles.SUPER_ADMIN)
                                  .map((role) => (
                                    <MenuItem key={role} value={role}>
                                      {role}
                                    </MenuItem>
                                  ))}
                              </TextField>
                              <Button
                                size="small"
                                onClick={() => handleUpdateRole(userOrg.userOrganizationId, newRole)}>
                                {localeCommon['save']}
                              </Button>
                              <Button size="small" onClick={() => setEditingRole(null)}>
                                {localeCommon['cancel']}
                              </Button>
                            </Box>
                          ) : (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={userOrg.status === UserStatus.DISABLED ? localeCommon['disabled'] : localeCommon['active']}
                                color={userOrg.status === UserStatus.DISABLED ? 'error' : 'success'}
                                size="small"
                              />
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {userOrg.role}
                              </Typography>
                              {canEditRole(userOrg.role) && (
                                <WolfTooltip title={localeUserOrgs['editRole']}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditingRole(userOrg.userOrganizationId);
                                      setNewRole(userOrg.role);
                                    }}>
                                    <EditOutlined fontSize="small" />
                                  </IconButton>
                                </WolfTooltip>
                              )}
                              {canRemoveFromOrg(userOrg.role) && (
                                <WolfTooltip title={localeUserOrgs['removeFromOrganization']}>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleRemoveFromOrganization(userOrg.userOrganizationId, userOrg.organizationId)
                                    }>
                                    <DeleteOutline fontSize="small" color="error" />
                                  </IconButton>
                                </WolfTooltip>
                              )}
                              {userOrg.status === UserStatus.ACTIVE && (
                                <WolfTooltip title={localeCommon['disable']}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDisableUserInOrganization(userOrg.organizationId)}>
                                    <NoAccounts fontSize="small" color="error" />
                                  </IconButton>
                                </WolfTooltip>
                              )}
                              {userOrg.status === UserStatus.DISABLED && (
                                <WolfTooltip title={localeCommon['enable']}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEnableUserInOrganization(userOrg.organizationId)}>
                                    <RestoreFromTrash fontSize="small" color="primary" />
                                  </IconButton>
                                </WolfTooltip>
                              )}
                              {userOrg.status === UserStatus.ACTIVE && (
                                userOrg.hasSeat ? (
                                  <WolfTooltip title={localeUsers['revokeSeat']}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRevokeSeat(userOrg.organizationId, userOrg.seatAssignmentId)}>
                                      <EventSeat fontSize="small" color="primary" />
                                    </IconButton>
                                  </WolfTooltip>
                                ) : (
                                  <WolfTooltip title={localeUsers['assignSeat']}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAssignSeat(userOrg.organizationId, userOrg.role)}>
                                      <EventSeat fontSize="small" sx={{ color: 'grey.400' }} />
                                    </IconButton>
                                  </WolfTooltip>
                                )
                              )}
                            </Box>
                          )}
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {/* Previously Belonged To Section */}
              {deletedUserOrganizations.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
                    {localeUserOrgs['previouslyBelongedTo']}
                  </Typography>
                  <List>
                    {deletedUserOrganizations.map((userOrg) => (
                      <ListItem key={userOrg.userOrganizationId} component={Paper} sx={{ mb: 1, bgcolor: '#f5f5f5' }}>
                        <ListItemText
                          primary={userOrg.organization.name}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="textSecondary">
                                {userOrg.organization.description}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {localeUserOrgs['lastRole']}: {userOrg.role} • {localeUserOrgs['removed']}:{' '}
                                {userOrg.deletedAt ? new Date(userOrg.deletedAt).toLocaleDateString() : localeUserOrgs['unknown']}
                              </Typography>
                            </Box>
                          }
                        />
                        <Box display="flex" alignItems="center" gap={1}>
                          <WolfTooltip title={localeUserOrgs['restoreToOrganization']}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleRestoreToOrganization(userOrg.organizationId, userOrg.role)}
                              sx={{ minWidth: 'auto' }}>
                              {localeUserOrgs['restore']}
                            </Button>
                          </WolfTooltip>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{localeCommon['close']}</Button>
        </DialogActions>
      </Dialog>

      {/* Add to Organization Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{localeUserOrgs['addUserToOrganization']}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              select
              label={localeCommon['organization']}
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              fullWidth>
              {availableOrganizations.map((org) => (
                <MenuItem key={org.organizationId} value={org.organizationId}>
                  {org.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={localeUsers['role']}
              value={addingRole}
              onChange={handleAddRoleChange}
              fullWidth>
              {Object.values(UserRoles)
                .filter((role) => isSuperAdmin || role !== UserRoles.SUPER_ADMIN)
                .map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>{localeCommon['cancel']}</Button>
          <Button onClick={handleAddToOrganization} variant="contained" disabled={!selectedOrgId}>
            {localeCommon['add']}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserOrganizationsModal;
